import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar } from "lucide-react";

export default function CalendarCreatePage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const presetPatient = sp.get("patient_id") || "";
  const presetRegimen = sp.get("regimen_id") || "";
  const [patients, setPatients] = useState([]);
  const [regimens, setRegimens] = useState([]);
  const [patientId, setPatientId] = useState(presetPatient);
  const [regimenId, setRegimenId] = useState(presetRegimen);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [selected, setSelected] = useState([]); // cycle indices; [] means all
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/regimens").then((r) => setRegimens(r.data));
  }, []);

  const regimen = useMemo(() => regimens.find((r) => r.id === regimenId), [regimens, regimenId]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await api.post("/calendars", { patient_id: patientId, regimen_id: regimenId, start_date: startDate, selected_cycles: selected.length ? selected : null });
      nav(`/calendars/${r.data.id}`);
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleCycle = (i) => setSelected((cur) => cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]);

  return (
    <div className="p-8 max-w-[900px]" data-testid="calendar-create-page">
      <Link to="/regimens" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 text-sm mb-4">
        <ArrowLeft className="w-4 h-4" />Back
      </Link>
      <h1 className="font-display text-3xl font-bold text-stone-900 mb-2">Generate patient calendar</h1>
      <p className="text-stone-500 mb-6">Pick a patient, regimen and start date. Cycle events are generated automatically.</p>
      {err && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

      <form onSubmit={submit} className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Patient*</label>
            <select data-testid="calendar-patient-select" required value={patientId} onChange={(e) => setPatientId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none">
              <option value="">— Select patient —</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.mrn})</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Start date*</label>
            <input data-testid="calendar-start-date-input" required type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-stone-700 mb-1.5 block">Regimen*</label>
          <select data-testid="calendar-regimen-select" required value={regimenId} onChange={(e) => { setRegimenId(e.target.value); setSelected([]); }}
            className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none">
            <option value="">— Select regimen —</option>
            {regimens.map((r) => <option key={r.id} value={r.id}>{r.name} — {r.disease_type}</option>)}
          </select>
        </div>

        {regimen && (
          <div>
            <label className="text-sm font-medium text-stone-700 mb-2 block">Cycles to include</label>
            <div className="flex flex-wrap gap-2">
              {regimen.cycles.map((c, idx) => (
                <button key={idx} type="button" onClick={() => toggleCycle(idx)}
                  data-testid={`cycle-toggle-${idx}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selected.includes(idx) || selected.length === 0
                      ? "bg-brand-500 text-white border-brand-500"
                      : "bg-white text-stone-600 border-stone-300 hover:bg-stone-50"
                  }`}>
                  {c.label} · {c.length_days}d
                </button>
              ))}
            </div>
            <div className="text-xs text-stone-500 mt-2">Leave all selected (or none) to include every cycle.</div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button data-testid="generate-calendar-submit" disabled={busy} type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-60">
            <Calendar className="w-4 h-4" />{busy ? "Generating…" : "Generate calendar"}
          </button>
        </div>
      </form>
    </div>
  );
}
